
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Services() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const skills = [
    {
      title: '資料分析',
      description: '運用 Python（Pandas、NumPy）進行資料清洗、探索性分析與統計推論，萃取有意義的洞察。',
      icon: 'ri-bar-chart-2-line',
      features: ['Pandas / NumPy', '探索性資料分析', '統計分析']
    },
    {
      title: 'SQL 資料庫查詢',
      description: '熟練撰寫複雜 SQL 查詢，包含 JOIN、CTE、Window Function，支援銷售趨勢與 RFM 等商業分析。',
      icon: 'ri-database-2-line',
      features: ['PostgreSQL / MySQL', 'CTE & Window Function', '商業數據查詢']
    },
    {
      title: '資料視覺化',
      description: '以 Tableau 和 Python 視覺化工具將數據轉化為清晰圖表與儀表板，協助決策者快速理解資料。',
      icon: 'ri-pie-chart-2-line',
      features: ['Tableau', 'Matplotlib / Seaborn', '儀表板設計']
    },
    {
      title: '法律實務背景',
      description: '歷任行政秘書與法務專員共 2 年半，負責訴訟案件流程管理、文書草擬審閱與跨機關協調，具備防制洗錢與金融市場合規研習認證。',
      icon: 'ri-shield-check-line',
      features: ['訴訟文書草擬與審閱', '跨機關溝通協調', '防制洗錢合規認證']
    },
    {
      title: '自動化處理',
      description: '以 Python 正則表達式與腳本自動擷取合約關鍵資訊，將重複性法律文書工作效率化。',
      icon: 'ri-file-search-line',
      features: ['Python 自動化', 'PDF 資訊擷取', 'Regex 解析']
    },
    {
      title: '英語溝通能力',
      description: 'TOEIC 795 分，具備閱讀英文技術文件、法律合約及與國際客戶溝通的實務能力。',
      icon: 'ri-translate-2',
      features: ['TOEIC 795（2025.12）', '英文技術文件閱讀', '跨語言溝通']
    }
  ];

  return (
    <section id="services" className="py-16 md:py-24 px-4 md:px-8 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div
            className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <span className="text-sm font-semibold text-orange-700">核心能力</span>
          </div>
          <h2
            className={`text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            我能為團隊<br />帶來什麼
          </h2>
          <p
            className={`text-base text-gray-600 max-w-2xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            結合法律專業背景與持續精進的數據技能，提供嚴謹且跨領域的分析視角
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {skills.map((skill, index) => (
            <div
              key={index}
              className={`group bg-gradient-to-br from-gray-50 to-orange-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 cursor-default border border-gray-100 hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-rose-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <i className={`${skill.icon} text-white text-3xl w-8 h-8 flex items-center justify-center`}></i>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300" style={{ fontFamily: 'Playfair Display, serif' }}>
                {skill.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                {skill.description}
              </p>

              <div className="space-y-2">
                {skill.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full group-hover:scale-125 transition-transform duration-300"></div>
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          className={`text-center mt-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '900ms' }}
        >
          <Link
            to="/skills"
            className="px-10 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            查看完整技能頁面
          </Link>
        </div>
      </div>
    </section>
  );
}
